/**
 * 広島県の背景ツアー「広島めぐり」コンテンツ。
 *
 * 軸は季節。5月の厳島神社から始めて、1年かけて翌年4月の生口島へ戻る。
 *
 * 原爆ドームについて。
 *  広島市公式の位置づけは「人類史上最初の原子爆弾による被爆の惨禍を伝える歴史の証人」
 *  「核兵器廃絶と恒久平和を求める誓いのシンボル」。この趣旨から外れない範囲で、
 *  事実だけを淡々と書く。観光消費的な語（絶景・映え・廃墟）、感嘆符、驚き型の見出し、
 *  犠牲者数をクイズのように扱うことは避ける。また 8月6日 を思わせる季節には置かない。
 *
 * そのほか、調査で出てきた「書いてはいけないこと」を反映している。
 *  - 雄橋の「日本三大天然橋」「世界三大天然橋」は公式で裏が取れない。書かない。
 *  - 広島城の天守は令和8年3月22日に閉城済み。「登って一望」と書かない。
 *  - 大鳥居のそばまで歩けるのは引き潮のときだけ。
 *  - 未来心の丘は耕三寺博物館の敷地内にある有料拝観施設。無料の公園のように書かない。
 *  - 鞆の浦は現役の漁港かつ住民の生活の場。
 */

import type { StageTour } from '../types';

export const hiroshimaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'itsukushima-otorii',
      name: '厳島神社 大鳥居',
      area: '廿日市市・5月の朝',
      image: '/assets/scenery/hiroshima/scenes/01_itsukushima_otorii.webp',
      hotspots: [
        {
          id: 'otorii-ookisa',
          x: 0.5,
          y: 0.3,
          title: '棟の長さ24.2メートル、約60トン',
          body: '棟の長さ24.2メートル、主柱の周囲9.9メートル、総重量はおよそ60トンとされ、木造の鳥居としては日本最大といわれます。主柱は耐久性や耐水性が高いクスノキの自然木、袖柱は杉。いまの大鳥居は平安時代から数えて9代目にあたるとされ、明治8年に再建されました。',
          source: 'https://www.miyajima.or.jp/sightseeing/ss_ootorii.html',
          mapQuery: '厳島神社 大鳥居',
        },
        {
          id: 'otorii-neoki',
          x: 0.44,
          y: 0.55,
          title: '海底には埋まっていない',
          body: '柱の根元は海中に置かれているだけです。地面を固めるため、それぞれの柱の下に松の丸太を打ち込んで安定性を高めており、これを千本杭と言います。屋根下の棟にあたる笠木・島木の中には、およそ4トン分の小石が詰め込まれています。',
          source: 'https://www.miyajima.or.jp/sightseeing/ss_ootorii.html',
          relatedItemId: 'yaki-gaki',
          mapQuery: '宮島 厳島神社',
        },
      ],
    },
    {
      id: 'tatara-ohashi',
      name: '多々羅大橋',
      area: '尾道市・6月の午前',
      image: '/assets/scenery/hiroshima/scenes/02_tatara_ohashi.webp',
      hotspots: [
        {
          id: 'tatara-shachou',
          x: 0.5,
          y: 0.36,
          title: '完成時は世界最長の斜張橋',
          body: '中央の支間は890メートル、主塔の高さは海面から226メートルあります。平成11年（1999年）5月1日に供用が始まり、完成当時は中央支間長890メートルで世界最長の斜張橋でした。',
          source:
            'https://www.jb-honshi.co.jp/corp_index/technology/introduction/introduction_tatara.html',
          mapQuery: '多々羅大橋',
        },
        {
          id: 'tatara-keikaku',
          x: 0.24,
          y: 0.58,
          title: '吊橋の計画を変えた理由',
          body: 'もとは吊橋として計画されましたが斜張橋に変更されました。おかげで生口島側の観音山を大きく削らずにすみました。フランスのノルマンディー橋と姉妹橋です。',
          source: 'https://www.jb-honshi.co.jp/shimanami/about/shimanami/p04.html',
          relatedItemId: 'hiroshima-lemon',
          mapQuery: 'しまなみ海道 多々羅大橋',
        },
      ],
    },
    {
      id: 'tomonoura',
      name: '鞆の浦の常夜燈',
      area: '福山市・7月の昼',
      image: '/assets/scenery/hiroshima/scenes/03_tomonoura.webp',
      hotspots: [
        {
          id: 'tomo-joyato',
          x: 0.48,
          y: 0.44,
          title: '海中の基礎から12.1メートル',
          body: '1859年（安政6年）に建てられた灯台です。海中の基礎から宝珠まで12.1メートルあり、現存する江戸時代の常夜燈としては最大級。',
          source: 'https://www.city.fukuyama.hiroshima.jp/site/miryoku2023/289568.html',
          relatedItemId: 'taimeshi',
          mapQuery: '鞆の浦 常夜燈',
        },
        {
          id: 'tomo-gangi',
          x: 0.32,
          y: 0.58,
          title: '港の施設が5点そろって残る',
          body: '常夜燈・雁木・波止・焚場跡・船番所跡という、近世の港に必要とされた5つの施設がそろって残っているのは、いまや鞆の浦だけです。雁木は、最大約4メートルにおよぶ潮の満ち引きに関係なく荷揚げができる石段です。',
          source: 'https://japan-heritage.bunka.go.jp/ja/stories/story065/',
          mapQuery: '鞆の浦',
        },
      ],
    },
    {
      id: 'sandankyo',
      name: '三段峡',
      area: '安芸太田町・8月の昼',
      image: '/assets/scenery/hiroshima/scenes/04_sandankyo.webp',
      hotspots: [
        {
          id: 'sandankyo-shinshoku',
          x: 0.48,
          y: 0.44,
          title: '深さ200メートルに及ぶ峡谷',
          body: '中国地方でもっとも高い八幡高原に源を発し、およそ10キロにわたって変化に富んだ峡谷をつくります。深さ200メートルに及ぶところがあるほど深く侵食され、淡紅色を帯びた岩の間を水が勢いよく流れます。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/2318',
          mapQuery: '三段峡',
        },
        {
          id: 'sandankyo-tokubetsu',
          x: 0.76,
          y: 0.28,
          title: '国の特別名勝',
          body: '大正14年10月8日に名勝、昭和28年11月14日に特別名勝へ指定されました。北方の植物から南方の植物まで、多様な植物相が残っています。遊歩道は通行規制が出ることがあるので、行く前に確認を。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/2318',
          mapQuery: '三段峡 正面口',
        },
      ],
    },
    {
      id: 'senkoji',
      name: '千光寺',
      area: '尾道市・9月の午後',
      image: '/assets/scenery/hiroshima/scenes/05_senkoji.webp',
      hotspots: [
        {
          id: 'senkoji-akadou',
          x: 0.5,
          y: 0.38,
          title: '断崖に張り出す「赤堂」',
          body: '標高140メートル、大宝山の中腹。舞台造りの本堂は貞享3年（1686年）のもので、別名「赤堂」とも呼ばれます。林芙美子も『放浪記』のなかで赤い千光寺の塔にふれています。',
          source: 'https://www.senkouji.jp/',
          mapQuery: '千光寺 尾道',
        },
        {
          id: 'senkoji-tsuzumiiwa',
          x: 0.76,
          y: 0.56,
          title: '音響遺産の鼓岩',
          body: '境内の「鼓岩（つづみいわ）」は、通称ポンポン岩。日本音響学会選奨の「音響遺産」に選定されています。',
          source: 'https://www.senkouji.jp/',
          relatedItemId: 'onomichi-ramen',
          mapQuery: '千光寺公園',
        },
      ],
    },
    {
      id: 'onbashi',
      name: '帝釈峡 雄橋',
      area: '庄原市・10月の夕',
      image: '/assets/scenery/hiroshima/scenes/06_onbashi.webp',
      hotspots: [
        {
          id: 'onbashi-shizen',
          x: 0.5,
          y: 0.44,
          title: '人が架けたのではない橋',
          body: '帝釈川の上に架かる、石灰岩の天然橋です。全長90メートル、幅18メートル、厚さ24メートル、川床からの高さ40メートル。昭和62年5月12日に国の天然記念物（地質鉱物）に指定されました。',
          source:
            'https://www.city.shobara.hiroshima.jp/main/education/shogaigakushu/cat02/02/post_895.html',
          mapQuery: '帝釈峡 雄橋',
        },
        {
          id: 'onbashi-mebashi',
          x: 0.76,
          y: 0.28,
          title: '雄橋と雌橋',
          body: '帝釈峡は大正12年3月7日に「帝釈川の谷」として国の名勝に指定されていて、その指定解説は雄橋と雌橋の二つの天然橋があると記しています。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/401/2316',
          mapQuery: '帝釈峡',
        },
      ],
    },
    {
      id: 'genbaku-dome',
      name: '原爆ドーム',
      area: '広島市・11月の午後',
      image: '/assets/scenery/hiroshima/scenes/07_genbaku_dome.webp',
      // この景色が映っているあいだ、花火・流れ星・画面の振動を止める。
      solemn: true,
      hotspots: [
        {
          id: 'dome-shoumei',
          x: 0.5,
          y: 0.36,
          title: '爆心地から約160メートル',
          body: '産業奨励館は爆心地から北西におよそ160メートルの至近距離で被爆し、爆風と熱線を浴びて大破しました。当時この建物の中にいた職員は、全員が即死しました。',
          source: 'https://www.city.hiroshima.lg.jp/atomicbomb-peace/1036658/1021107/1015067.html',
          mapQuery: '原爆ドーム',
        },
        {
          id: 'dome-moto',
          x: 0.26,
          y: 0.4,
          title: 'もとの名は広島県産業奨励館',
          body: 'チェコの建築家ヤン・レツルの監督で「広島県物産陳列館」として建てられ、のちに「広島県立商品陳列所」「広島県産業奨励館」と改称されました。特徴のある緑色のドームで市民に親しまれていた建物です。',
          source:
            'https://www.city.hiroshima.lg.jp/atomicbomb-peace/fukko/1021101/1026920/1026921/1020866.html',
          mapQuery: '広島平和記念公園',
        },
        {
          id: 'dome-hozon',
          x: 0.74,
          y: 0.42,
          title: '1996年に世界文化遺産',
          body: '平成8年（1996年）12月、メキシコで開かれた世界遺産委員会で、原爆ドームの世界遺産登録が決まりました。それに先立つ昭和42年（1967年）には、国内外から寄せられた募金によって第1回の保存工事が行われています。',
          source: 'https://www.city.hiroshima.lg.jp/atomicbomb-peace/1036658/1021107/1015067.html',
          mapQuery: '原爆ドーム 元安川',
        },
      ],
    },
    {
      id: 'hiroshimajo',
      name: '広島城',
      area: '広島市・12月の夕',
      image: '/assets/scenery/hiroshima/scenes/08_hiroshimajo.webp',
      hotspots: [
        {
          id: 'hiroshimajo-mouri',
          x: 0.5,
          y: 0.34,
          title: '太田川の三角州に築いた平城',
          body: '毛利輝元が太田川河口の三角州に築いた平城で、天正17年（1589年）から約2年の歳月をかけて完成しました。「鯉城」とも呼ばれます。',
          source: 'https://www.pref.hiroshima.lg.jp/soshiki_file/tatemonogatari/page049.html',
          mapQuery: '広島城',
        },
        {
          id: 'hiroshimajo-heisa',
          x: 0.42,
          y: 0.47,
          title: 'いまは中に入れません',
          body: '天守は原爆によって倒壊し、昭和33年に倒壊以前の外観を模して鉄筋コンクリートで再建されました。令和8年3月22日をもって閉城し、博物館としての役目は、令和8年度末の開館を目指して整備中の「広島城三の丸歴史館」に引き継がれます。',
          source:
            'https://www.city.hiroshima.lg.jp/tourism-culture/history/1026883/1026891/1044406.html',
          mapQuery: '広島城 三の丸',
        },
      ],
    },
    {
      id: 'yamato-museum',
      name: '大和ミュージアム',
      area: '呉市・1月の午後',
      image: '/assets/scenery/hiroshima/scenes/09_yamato_museum.webp',
      // 戦没者3,056名を扱う館なので、原爆ドームと同じ扱いにする。
      solemn: true,
      hotspots: [
        {
          id: 'yamato-tenji',
          x: 0.5,
          y: 0.42,
          title: '10分の1の戦艦「大和」',
          body: '1階の大和ひろばには、全長26.3メートルの10分の1戦艦「大和」が、設計図や写真、潜水調査の水中映像などをもとに再現されています。零式艦上戦闘機六二型などの実物資料も展示され、呉鎮守府の開庁から工廠とともに成長した呉の街の歴史を紹介しています。',
          source: 'https://yamato-museum.com/%E9%A4%A8%E5%86%85%E6%A1%88%E5%86%85/',
          relatedItemId: 'kure-kaiji-curry',
          mapQuery: '大和ミュージアム',
        },
        {
          id: 'yamato-rekishi',
          x: 0.3,
          y: 0.44,
          title: '呉海軍工廠で建造された艦',
          body: '戦艦「大和」は呉海軍工廠で建造され、昭和16年12月16日に竣工しました。昭和20年4月7日に沈没しています。最後に乗り組んでいたのは3,332人でした。',
          source: 'https://yamato-museum.com/yamato-data/',
          mapQuery: '呉市海事歴史科学館',
        },
      ],
    },
    {
      id: 'miraishin-no-oka',
      name: '未来心の丘',
      area: '尾道市・4月の朝',
      image: '/assets/scenery/hiroshima/scenes/10_miraishin_no_oka.webp',
      hotspots: [
        {
          id: 'miraishin-daiirseki',
          x: 0.5,
          y: 0.44,
          title: '5,000平方メートルの白大理石',
          body: '彫刻家・杭谷一東の作。使われている大理石はすべて、氏のアトリエがあるイタリア・カッラーラで採掘されてコンテナ船で運ばれた白大理石です。面積は5,000平方メートル。',
          source: 'https://www.kousanji.or.jp/hill_of_hope.html',
          mapQuery: '未来心の丘',
        },
        {
          id: 'miraishin-17nen',
          x: 0.76,
          y: 0.28,
          title: '構想から完成まで17年',
          body: '1988年に着工し、2000年に第1期がオープンしました。「光明の塔」「白獅子の塔」「亀玉の舞台」「風の四季」などのモニュメントがあります。耕三寺博物館の敷地内にある有料の拝観施設です。',
          source: 'https://www.kousanji.or.jp/hill_of_hope.html',
          mapQuery: '耕三寺博物館',
        },
      ],
    },
  ],
};
